import { ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import * as fns from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MDBIcon } from 'mdb-react-ui-kit';
import { useWindowSize } from '../../../hooks/useWindowSize';
import Lang from '../../../service/lang';
import { SalesReportResponse } from './SalesReportModal';

const COLOR_SCHEME: Record<string, Array<[number, string]>> = {
    day: [
        [0, '#fc3f5244'],
        [2, '#95eb1544'],
    ],
    month: [
        [0, '#fc3f5244'],
        [10, '#95eb1544'],
    ],
    hour: [
        [0, '#fc3f5244'],
        [2, '#95eb1544'],
    ],
};

type SalesRow = {
    id: number;
    name: string;
    names: string[];
    isExtra: boolean;
    sales: Array<{ cash: number; card: number; total: number }>;
    total: { cash: number; card: number; total: number };
};

type SalesTotalsRow = SalesRow & { isTotals: true };

export function SalesReportTable({
    data,
    filter,
}: {
    data: SalesReportResponse;
    filter: { cash: boolean; card: boolean; total: boolean };
}) {
    const { t } = useTranslation();
    const { innerWidth } = useWindowSize();
    const isMobile = useMemo(() => innerWidth < 700, [innerWidth]);

    const rows = useMemo<SalesRow[]>(() => {
        const values = Array.isArray(data.values)
            ? data.values.map((v, idx) => ({ id: idx, ...v }))
            : Object.entries(data.values).map(([key, value]) => ({
                  id: Number(key),
                  ...value,
              }));

        return values.map((value) => {
            const sales = value.sales.map(([cash, card]) => ({
                cash,
                card,
                total: cash + card,
            }));
            const total = sales.reduce(
                (prev, crt) => ({
                    cash: prev.cash + crt.cash,
                    card: prev.card + crt.card,
                    total: prev.total + crt.total,
                }),
                { cash: 0, card: 0, total: 0 },
            );

            return {
                id: value.id,
                name: value.names[0] ?? '?',
                names: value.names,
                isExtra: value.isExtra,
                sales,
                total,
            };
        });
    }, [data.values]);

    const totalsRow = useMemo<SalesTotalsRow>(() => {
        const totalsSales = data.intervals.map(() => ({
            cash: 0,
            card: 0,
            total: 0,
        }));
        const totals = rows.reduce(
            (prev, row) => {
                row.sales.forEach((item, idx) => {
                    totalsSales[idx].cash += item.cash;
                    totalsSales[idx].card += item.card;
                    totalsSales[idx].total += item.total;
                });
                prev.cash += row.total.cash;
                prev.card += row.total.card;
                prev.total += row.total.total;
                return prev;
            },
            { cash: 0, card: 0, total: 0 },
        );

        return {
            id: -1,
            name: `${t('total')} (${rows.length})`,
            names: [],
            isExtra: false,
            sales: totalsSales,
            total: totals,
            isTotals: true,
        };
    }, [data.intervals, rows, t]);

    const avgTotals = useMemo(() => {
        const totalRows = Math.max(1, rows.length);
        return {
            cash: totalsRow.total.cash / totalRows,
            card: totalsRow.total.card / totalRows,
            total: totalsRow.total.total / totalRows,
        };
    }, [rows.length, totalsRow.total]);

    const intervalLabels = useMemo(() => {
        const resolution = data.resolution ?? 'day';
        return data.intervals.map((interval) => {
            const d = new Date(interval);
            if (resolution == 'hour') {
                return fns.format(d, 'HH:mm', {
                    locale: Lang.instance.getCurrentLocale(),
                });
            }
            if (resolution == 'day') {
                return `${d.getDate().toString().padStart(2, '0')}/${(
                    d.getMonth() + 1
                )
                    .toString()
                    .padStart(2, '0')}`;
            }
            if (resolution == 'month') {
                return fns.format(d, 'MMMM', {
                    locale: Lang.instance.getCurrentLocale(),
                });
            }
            return d.toLocaleString();
        });
    }, [data.intervals, data.resolution]);

    const columnDefs = useMemo<(ColDef<SalesRow> | ColGroupDef<SalesRow>)[]>(
        () => [
            {
                headerName: t('name'),
                valueGetter: (row) => row.data?.name,
                tooltipValueGetter: (row) =>
                    row.data?.names.slice(1).join(', ') || undefined,
                width: 240,
                resizable: true,
                sortable: true,
                pinned: isMobile ? undefined : 'left',
                cellRenderer: ({ value, data, node }) => (
                    <span>
                        {!node.rowPinned && (
                            <span className="me-1 text-primary">
                                <MDBIcon
                                    icon={
                                        data.source == 'group'
                                            ? 'location-dot'
                                            : 'server'
                                    }
                                />
                            </span>
                        )}
                        <span
                            style={{
                                fontWeight: node.rowPinned ? 'bold' : '',
                                ...(data.isExtra
                                    ? {
                                          textDecoration: 'line-through',
                                          fontStyle: 'italic',
                                      }
                                    : {}),
                            }}
                        >
                            {value}
                        </span>
                        {data.prevNames && (
                            <span className="ms-1 text-warning">
                                <MDBIcon icon="asterisk" size="xs" />
                            </span>
                        )}
                    </span>
                ),
            },
            {
                field: 'total',
                headerName: t('total'),
                width: data.resolution == 'month' ? 200 : 170,
                resizable: true,
                sortable: true,
                pinned: isMobile ? undefined : 'right',
                cellRenderer: ({ value, node }) => {
                    const ratio =
                        filter.total || (filter.cash && filter.card)
                            ? value.total /
                              (totalsRow.total.total / rows.length)
                            : filter.cash
                              ? value.cash / totalsRow.total.cash / rows.length
                              : filter.card
                                ? value.card /
                                  (totalsRow.total.card / rows.length)
                                : null;
                    let color = '';
                    if (ratio == null) {
                        color = '';
                    } else if (ratio < 1) {
                        color = `rgba(255,0,0,${0.5 * (1 - ratio)})`;
                    } else {
                        color = `rgba(0,255,0,${Math.min(0.8, 0.5 * (ratio - 1))})`;
                    }

                    return (
                        <div
                            className="d-flex flex-row justify-content-center h-100 w-100 align-items-center px-2"
                            style={{ background: node.rowPinned ? '' : color }}
                        >
                            <div
                                className="d-flex flex-column justify-content-center h-100 flex-fill"
                                style={{
                                    fontWeight: node.rowPinned ? 'bold' : '',
                                }}
                            >
                                {(filter.cash || filter.card) && (
                                    <div className="d-flex flex-row w-100">
                                        {filter.cash && (
                                            <span
                                                style={{ lineHeight: '1.1em' }}
                                                className="flex-fill d-flex justify-content-center align-items-center"
                                            >
                                                <MDBIcon
                                                    icon="coins"
                                                    size="sm"
                                                    color="primary"
                                                    className="me-1"
                                                />
                                                {value.cash}
                                            </span>
                                        )}
                                        {filter.card && (
                                            <span
                                                style={{ lineHeight: '1.1em' }}
                                                className="flex-fill d-flex justify-content-center align-items-center"
                                            >
                                                <MDBIcon
                                                    icon="credit-card"
                                                    size="sm"
                                                    color="primary"
                                                    className="me-1"
                                                />
                                                {value.card}
                                            </span>
                                        )}
                                    </div>
                                )}
                                {filter.total && (
                                    <span
                                        style={{ lineHeight: '1.1em' }}
                                        className={
                                            'd-flex flex-row justify-content-center fw-bold  w-100 ' +
                                            (filter.cash || filter.card
                                                ? 'text-success'
                                                : '')
                                        }
                                    >
                                        {value.total}
                                    </span>
                                )}
                            </div>
                            {!node.rowPinned &&
                                ratio !== null &&
                                !value.hidePercent && (
                                    <span
                                        className="ms-2 text-muted"
                                        style={{ fontSize: '0.65rem' }}
                                    >
                                        {(ratio * 100).toFixed(1)}%
                                    </span>
                                )}
                        </div>
                    );
                },
                cellStyle: () => ({ padding: 0 }),
                valueFormatter: ({ value }) =>
                    `${value.cash}|${value.card}|${value.total}`,
                comparator: (a, b) =>
                    (filter.total || (filter.cash && filter.card)
                        ? a.total
                        : filter.cash
                          ? a.cash
                          : a.card) -
                    (filter.total || (filter.cash && filter.card)
                        ? b.total
                        : filter.cash
                          ? b.cash
                          : b.card),
            },
            {
                headerName:
                    {
                        month: t('monthly_breakdown'),
                        day: t('daily_breakdown'),
                        hour: t('hourly_breakdown'),
                    }[data.resolution] || '?',
                children: data.intervals.map((int, idx) => {
                    let name = '?';
                    const d = new Date(int);
                    if (data.resolution == 'hour')
                        name = fns.format(d, 'HH:mm', {
                            locale: Lang.instance.getCurrentLocale(),
                        });
                    if (data.resolution == 'day')
                        name = `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
                    if (data.resolution == 'month')
                        name = fns.format(d, 'MMMM', {
                            locale: Lang.instance.getCurrentLocale(),
                        });

                    return {
                        field: `i${idx}`,
                        headerName: name,
                        headerTooltip: d.toLocaleString(),
                        width: data.resolution == 'month' ? 130 : 100,
                        resizable: true,
                        sortable: false,
                        cellRenderer: ({ node }) => {
                            const { cash, card, total } = node.data.sales[idx];

                            const cashColor = COLOR_SCHEME[
                                data.resolution
                            ].find((pair) => cash <= pair[0]);
                            const cardColor = COLOR_SCHEME[
                                data.resolution
                            ].find((pair) => card <= pair[0]);
                            const totalColor = COLOR_SCHEME[
                                data.resolution
                            ].find((pair) => total <= pair[0]);
                            return (
                                <div
                                    className="d-flex flex-column justify-content-center h-100"
                                    style={{
                                        fontWeight: node.rowPinned
                                            ? 'bold'
                                            : '',
                                    }}
                                >
                                    {(filter.cash || filter.card) && (
                                        <div className="d-flex flex-row w-100">
                                            {filter.cash && (
                                                <span
                                                    style={{
                                                        lineHeight: '1.1em',
                                                        background: cashColor
                                                            ? cashColor[1]
                                                            : '',
                                                    }}
                                                    className="flex-fill d-flex justify-content-center"
                                                >
                                                    {cash}
                                                </span>
                                            )}
                                            {filter.card && (
                                                <span
                                                    style={{
                                                        lineHeight: '1.1em',
                                                        background: cardColor
                                                            ? cardColor[1]
                                                            : '',
                                                    }}
                                                    className="flex-fill d-flex justify-content-center"
                                                >
                                                    {card}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {filter.total && (
                                        <span
                                            style={{
                                                lineHeight: '1.1em',
                                                background: totalColor
                                                    ? totalColor[1]
                                                    : '',
                                            }}
                                            className={
                                                'd-flex flex-row justify-content-center  w-100 ' +
                                                (filter.cash || filter.card
                                                    ? 'text-success'
                                                    : '')
                                            }
                                        >
                                            {total}
                                        </span>
                                    )}
                                </div>
                            );
                        },
                        cellStyle: () => {
                            return {
                                padding: 0,
                            };
                        },
                        valueFormatter: ({ node }) => {
                            if (!node?.data) return '';
                            const { cash, card, total } = node.data.sales[idx];
                            return `${cash}|${card}|${total}`;
                        },
                        comparator: (a, b) =>
                            a.sales[idx].total - b.sales[idx].total,
                    };
                }),
            },
        ],
        [
            t,
            isMobile,
            intervalLabels,
            data.resolution,
            avgTotals.total,
            filter.cash,
            filter.card,
            filter.total,
            rows.length,
            totalsRow.total.cash,
            totalsRow.total.card,
            totalsRow.total.total,
        ],
    );

    return (
        <AgGridReact<SalesRow>
            columnDefs={columnDefs}
            enableBrowserTooltips
            rowData={rows}
            pinnedBottomRowData={[totalsRow]}
            suppressMovableColumns
            headerHeight={110}
        />
    );
}
